import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import apiUrl from "../Apiurl";
import stilePulsanti from "../interfaccia/stilePulsanti";

// Traduzione massiva della selezione della pagina (piano 63, T16): stima con
// cache e quota, poi job `traduci_testi` visibile in Job massivi.
const SITI = ["VIG", "BOT"];
const LINGUE = { en: "Inglese", fr: "Francese" };
const INIZIALE = { fonti: ["VIG", "BOT"], lingue: ["en", "fr"], modalita: "mancanti", accoda: false };

function numero(value) {
  return Number(value ?? 0).toLocaleString("it-IT");
}

function erroreRisposta(error, fallback) {
  return error?.response?.data?.details?.join(" ") || error?.response?.data?.error || error?.message || fallback;
}

export default function TraduzioneMassiva({ selection, utente }) {
  const { getAccessTokenSilently } = useAuth0();
  const permessi = utente?.permessiAPI || {};
  const puoTradurre = Boolean(permessi["usage:googleapis"] && permessi["update:database"]);
  const puoAccodare = Boolean(permessi["update:prestashop"]);
  const [opzioni, setOpzioni] = useState(INIZIALE);
  const [stima, setStima] = useState(null);
  const [conferma, setConferma] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState("");
  const [job, setJob] = useState(null);

  // Una stima vale solo per la selezione e le opzioni con cui e' stata fatta
  // (la selezione arriva come oggetto nuovo a ogni render: si confronta il contenuto).
  const chiaveSelezione = JSON.stringify(selection);
  useEffect(() => { setStima(null); setConferma(false); setJob(null); }, [chiaveSelezione, opzioni]);

  const toggle = (chiave, valore) => setOpzioni((attuali) => {
    const lista = attuali[chiave].includes(valore) ? attuali[chiave].filter((item) => item !== valore) : [...attuali[chiave], valore];
    return { ...attuali, [chiave]: lista };
  });
  const payload = { ...opzioni, accoda: opzioni.accoda && puoAccodare };
  const opzioniValide = opzioni.fonti.length > 0 && opzioni.lingue.length > 0;

  async function richiesta(eseguire) {
    setBusy(true);
    setErrore("");
    try {
      const token = await getAccessTokenSilently();
      await eseguire({ headers: { Authorization: `Bearer ${token}` } });
    } finally {
      setBusy(false);
    }
  }

  const calcolaStima = () => richiesta(async (options) => {
    try {
      const response = await axios.post(`${apiUrl}/prodotti/translate/stima`, { filters: selection, payload }, options);
      setStima(response.data?.data ?? null);
    } catch (error) {
      setErrore(erroreRisposta(error, "Stima non disponibile."));
    }
  });

  const avvia = () => richiesta(async (options) => {
    try {
      const response = await axios.post(`${apiUrl}/moda/articoli/bulk-jobs`,
        { jobType: "traduci_testi", filters: selection, payload }, options);
      setJob({ ...response.data?.job, deduped: response.data?.deduped === true });
    } catch (error) {
      setErrore(erroreRisposta(error, "Avvio del job non riuscito."));
    }
  });

  const motivoBlocco = !puoTradurre ? "Per avviare la traduzione massiva servono i permessi usage:googleapis e update:database."
    : !opzioniValide ? "Scegli almeno un sito e una lingua." : "";
  const richiedeConferma = opzioni.modalita === "ritraduci";
  const avviabile = !motivoBlocco && stima?.sufficiente && stima.articoliDaTradurre > 0 && (!richiedeConferma || conferma) && !job;

  return <section className="traduzioni-card sfondoBiancoTema ba bordoColAlt br3 pa3 mt3" aria-label="Traduzione della selezione">
    <h2 className="mt0">Traduci la selezione</h2>
    <p className="f6">Traduce gli articoli della selezione corrente eleggibili sui siti scelti. Il job usa la cache e la quota
      del canale; i testi già in cache non consumano caratteri.</p>
    <div className="traduzioni-filters">
      <fieldset><legend>Siti</legend>
        {SITI.map((sito) => <label key={sito} className="mr3"><input type="checkbox" checked={opzioni.fonti.includes(sito)}
          onChange={() => toggle("fonti", sito)} disabled={busy} /> {sito}</label>)}
      </fieldset>
      <fieldset><legend>Lingue</legend>
        {Object.entries(LINGUE).map(([lingua, nome]) => <label key={lingua} className="mr3"><input type="checkbox"
          checked={opzioni.lingue.includes(lingua)} onChange={() => toggle("lingue", lingua)} disabled={busy} /> {nome}</label>)}
      </fieldset>
      <fieldset><legend>Campi</legend>
        <label className="mr3"><input type="radio" name="modalita" checked={opzioni.modalita === "mancanti"} disabled={busy}
          onChange={() => setOpzioni({ ...opzioni, modalita: "mancanti" })} /> Solo mancanti</label>
        <label><input type="radio" name="modalita" checked={opzioni.modalita === "ritraduci"} disabled={busy}
          onChange={() => setOpzioni({ ...opzioni, modalita: "ritraduci" })} /> Ritraduci tutto</label>
      </fieldset>
      <label><input type="checkbox" checked={payload.accoda} disabled={busy || !puoAccodare}
        onChange={(event) => setOpzioni({ ...opzioni, accoda: event.target.checked })} /> Accoda la sync dei testi sui siti
        {!puoAccodare && <span className="f7"> (serve update:prestashop)</span>}</label>
    </div>
    {motivoBlocco && <p role="status">{motivoBlocco}</p>}
    <div className="mt2">
      <button type="button" className={stilePulsanti.buttonClassesWhite} onClick={calcolaStima}
        disabled={busy || Boolean(motivoBlocco)}>{busy && !stima ? "Stima in corso..." : "Stima"}</button>
    </div>
    {stima && <div className="mt2" aria-label="Stima traduzione">
      <p className="mb1">Articoli eleggibili: <strong>{numero(stima.articoli)}</strong> · da tradurre: <strong>{numero(stima.articoliDaTradurre)}</strong>
        {stima.segmentiDaGlossario > 0 && <> · campi dal glossario: {numero(stima.segmentiDaGlossario)}</>}</p>
      <p className="mb1">Caratteri stimati: <strong>{numero(stima.unitaStimate)}</strong> sul canale {stima.canale} ·
        {stima.residuo === null ? " quota illimitata" : ` residuo del mese: ${numero(stima.residuo)}`}</p>
      {stima.articoliNonTrovati > 0 && <p className="f6 mb1">{numero(stima.articoliNonTrovati)} articoli senza scheda in Domino verranno segnalati come errori.</p>}
      {!stima.sufficiente && <p role="alert">La stima supera la quota residua: restringi la selezione o attendi il prossimo periodo.</p>}
      {stima.sufficiente && stima.articoliDaTradurre === 0 && <p role="status">Nulla da tradurre con queste opzioni.</p>}
      {richiedeConferma && stima.articoliDaTradurre > 0 && <label className="db mt2"><input type="checkbox" checked={conferma}
        onChange={(event) => setConferma(event.target.checked)} /> Ho capito: "Ritraduci tutto" sostituisce anche i testi
        modificati a mano.</label>}
      <button type="button" className={`${stilePulsanti.buttonClassesGreen} mt2`} onClick={avvia} disabled={busy || !avviabile}>
        Avvia traduzione</button>
    </div>}
    {errore && <p role="alert">{errore}</p>}
    {job && <p role="status">{job.deduped ? "Esiste già un job identico in corso" : "Job avviato"}: <code>{job.jobId}</code>.
      {" "}<a href="/jobMassiviPubblicazione">Segui l'avanzamento in Job massivi</a>.</p>}
  </section>;
}
